import { Link } from 'react-router-dom';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-100 text-gray-900 p-8 mt-[60px]">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-10">
        <h1 className="text-4xl font-bold text-center mb-8 text-black">Privacy Policy</h1>
        <p className="mb-6 text-lg">
          Evidence Cleaning (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) respects your privacy. This Privacy Policy describes how we collect, use, store, and protect information when customers, website visitors, employees, independent contractors, and independent service professionals interact with our website, cleaning services, and business communications.
        </p>
        <p className="mb-6 text-lg">
          For additional provisions governing the use of our website and SMS programs, please review our{' '}
          <Link to="/terms" className="text-blue-600 underline">
            Terms of Service
          </Link>
          .
        </p>

        <div className="border-l-4 border-gray-400 pl-4 mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-black">Information We May Collect</h2>
          <p className="mb-4 text-lg">
            Depending on your relationship with Evidence Cleaning, we may collect:
          </p>
          <ul className="list-disc pl-6 mb-6 text-lg space-y-2">
            <li>Your name;</li>
            <li>Mobile telephone number;</li>
            <li>Email address;</li>
            <li>Service or billing address;</li>
            <li>Appointment and scheduling information;</li>
            <li>Cleaning-service details;</li>
            <li>Payment or invoice information;</li>
            <li>Customer-support communications;</li>
            <li>Employee or contractor scheduling and assignment information;</li>
            <li>SMS consent records;</li>
            <li>Communication preferences; and</li>
            <li>Information voluntarily provided during a phone call, form submission, booking, onboarding process, or other business interaction.</li>
          </ul>
        </div>

        <div className="border-l-4 border-gray-400 pl-4 mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-black">How We Use Information</h2>
          <p className="mb-4 text-lg">We may use personal information to:</p>
          <ul className="list-disc pl-6 mb-6 text-lg space-y-2">
            <li>Schedule and provide cleaning services;</li>
            <li>Send appointment confirmations and reminders;</li>
            <li>Respond to customer inquiries;</li>
            <li>Process payments and invoices;</li>
            <li>Assign and coordinate cleaning work;</li>
            <li>Communicate service addresses and access instructions;</li>
            <li>Send schedule changes and operational notices;</li>
            <li>Provide support while services are being performed;</li>
            <li>Maintain consent and opt-out records;</li>
            <li>Protect our website, customers, workers, and business; and</li>
            <li>Comply with applicable legal and regulatory obligations.</li>
          </ul>
        </div>

        <div className="border-l-4 border-gray-400 pl-4 mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-black">SMS Communications</h2>
          <p className="mb-6 text-lg">
            Evidence Cleaning operates informational SMS programs for customers, employees, independent contractors, and independent cleaning professionals who affirmatively opt in.
          </p>

          <h3 className="text-xl font-semibold mb-3 text-black">Customer SMS Communications</h3>
          <p className="mb-4 text-lg">Customers may receive messages concerning:</p>
          <ul className="list-disc pl-6 mb-6 text-lg space-y-2">
            <li>Appointment confirmations;</li>
            <li>Appointment reminders;</li>
            <li>Scheduling or rescheduling;</li>
            <li>Arrival and service updates;</li>
            <li>Payment or invoice information; and</li>
            <li>Customer support.</li>
          </ul>

          <h3 className="text-xl font-semibold mb-3 text-black">Employee and Independent Service Professional SMS Communications</h3>
          <p className="mb-4 text-lg">Employees and independent service professionals may receive messages concerning:</p>
          <ul className="list-disc pl-6 mb-4 text-lg space-y-2">
            <li>Work schedules;</li>
            <li>Cleaning assignments;</li>
            <li>Assignment availability;</li>
            <li>Service locations;</li>
            <li>Access instructions;</li>
            <li>Schedule changes;</li>
            <li>Arrival and completion confirmations;</li>
            <li>Operational support; and</li>
            <li>Other necessary job-related information.</li>
          </ul>
          <p className="mb-6 text-lg">
            The workforce SMS program is not used to send marketing or promotional content.
          </p>

          <h3 className="text-xl font-semibold mb-3 text-black">SMS Consent</h3>
          <p className="mb-4 text-lg">
            Consent may be collected through a website form, written or electronic agreement, booking process, or documented verbal opt-in process.
          </p>
          <p className="mb-4 text-lg">
            When employees or independent service professionals provide verbal consent, Evidence Cleaning maintains a record of the recipient&apos;s name, mobile number, date and method of consent, the representative who collected it, and confirmation that affirmative consent was received.
          </p>
          <p className="mb-4 text-lg">
            Providing a phone number alone does not automatically constitute consent to receive recurring SMS messages.
          </p>
          <p className="mb-6 text-lg">
            A person who initiates a text conversation with Evidence Cleaning may receive a direct response to that inquiry. Ongoing recurring messages require an applicable opt-in.
          </p>

          <h3 className="text-xl font-semibold mb-3 text-black">Message Frequency and Charges</h3>
          <p className="mb-4 text-lg">
            Message frequency varies based on appointments, assignments, scheduling activity, and the recipient&apos;s interactions with Evidence Cleaning.
          </p>
          <p className="mb-6 text-lg">Message and data rates may apply.</p>

          <h3 className="text-xl font-semibold mb-3 text-black">Opting Out</h3>
          <p className="mb-4 text-lg">
            Recipients may opt out of SMS communications at any time by replying <strong>STOP</strong>.
          </p>
          <p className="mb-4 text-lg">
            For assistance, recipients may reply <strong>HELP</strong> or contact Evidence Cleaning directly.
          </p>
          <p className="mb-6 text-lg">
            Workers who opt out should contact their manager to arrange another approved way to receive necessary work communications.
          </p>
        </div>

        <div className="border-l-4 border-gray-400 pl-4 mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-black">Mobile Information and Consent Data</h2>
          <p className="mb-4 text-lg">
            Evidence Cleaning does not sell, rent, transfer, or share mobile phone numbers, SMS opt-in information, or text-messaging consent with third parties or affiliates for marketing or promotional purposes. This information will not be transferred to any external organization for marketing or promotional use. We may disclose information to service providers solely when necessary to operate our communications and services, provided they are prohibited from using it for their own marketing purposes.
          </p>
          <p className="mb-6 text-lg">
            Mobile information and messaging consent are used only to operate the applicable Evidence Cleaning communication program, provide requested services, coordinate work, maintain consent records, and meet legal or carrier requirements.
          </p>
        </div>

        <div className="border-l-4 border-gray-400 pl-4 mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-black">Data Retention</h2>
          <p className="mb-4 text-lg">
            We retain personal information and SMS consent records for as long as reasonably necessary to provide services, manage business operations, document consent, resolve disputes, and comply with legal or regulatory requirements.
          </p>
          <p className="mb-6 text-lg">
            SMS opt-out records may be retained to ensure that we continue to honor a recipient&apos;s request not to receive messages.
          </p>
        </div>

        <div className="border-l-4 border-gray-400 pl-4 mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-black">Information Security</h2>
          <p className="mb-6 text-lg">
            We use reasonable administrative and technical measures intended to protect personal information. However, no electronic transmission or storage method can be guaranteed to be completely secure.
          </p>
        </div>

        <div className="border-l-4 border-gray-400 pl-4 mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-black">Your Choices</h2>
          <p className="mb-4 text-lg">You may contact us to:</p>
          <ul className="list-disc pl-6 mb-6 text-lg space-y-2">
            <li>Update inaccurate contact information;</li>
            <li>Ask questions about this Privacy Policy;</li>
            <li>Change communication preferences; or</li>
            <li>Request assistance with an SMS opt-out.</li>
          </ul>
        </div>

        <div className="border-l-4 border-gray-400 pl-4 mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-black">Contact Us</h2>
          <p className="mb-2 text-lg">Evidence Cleaning</p>
          <p className="mb-2 text-lg">
            Email:{' '}
            <a href="mailto:contact@worldwideevidence.com" className="text-blue-600 underline">
              contact@worldwideevidence.com
            </a>
          </p>
          <p className="mb-6 text-lg">
            Phone:{' '}
            <a href="tel:7255774523" className="text-blue-600 underline">
              (725)-577-4523
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
